import React, { useEffect, useRef, useState, useCallback } from 'react';
import HuitziMascot from './HuitziMascot';
import HuitziBubble from './HuitziBubble';
import HuitziChat from './HuitziChat';

/**
 * HuitziCompanion — floating colibrí assistant that flies around the site.
 *
 * Behavior:
 *   - Home base is the bottom-right corner.
 *   - While the chat is closed, Huitzi periodically flies to a new perch
 *     around the viewport edges (never over the dead-center content),
 *     facing the direction of travel — like a real hummingbird flitting
 *     between flowers.
 *   - DRAGGABLE: the user can grab Huitzi (mouse or touch) and park him
 *     anywhere. A user-placed Huitzi is "pinned": the roaming loop stops
 *     so he never flies back into the way. Opening the chat flies him
 *     home (the chat anchors there); closing it returns him to the pin.
 *   - MOBILE: smaller mascot (76px under 600px viewports) and tighter
 *     margin so he doesn't cover a third of a phone screen.
 *   - Clicking the mascot (a press without movement) toggles the chat.
 *   - Speech-bubble tips appear only while parked at home.
 *   - Respects prefers-reduced-motion: stays parked, no roaming.
 *
 * Mechanics: a fixed home container at the bottom-right holds both the
 * chat (anchored, never moves) and a "flyer" group that is translated by
 * an (x, y) offset away from the home corner. Negative offsets move the
 * mascot up/left across the viewport.
 */

const SIZE_DESKTOP = 120;
const SIZE_MOBILE = 76;
const MARGIN_DESKTOP = 24;
// En móvil el colibrí se posa POR ENCIMA de la barra inferior de navegación
// (62px + borde del iPhone): antes se sentaba justo sobre las pestañas y
// tapaba «Registrar», la acción más usada del producto.
const MARGIN_MOBILE = 12;
const MOBILE_NAV_CLEARANCE = 74;
const MOBILE_BP = 600;
const DRAG_THRESHOLD = 8; // px of movement before a press becomes a drag

const isMobileViewport = () =>
  typeof window !== 'undefined' && window.innerWidth < MOBILE_BP;

const HuitziCompanion = ({
  isAuthenticated = false,
  contextTips = null,
  enabled = true,
  onAction,
}) => {
  const [chatOpen, setChatOpen] = useState(false);
  const [bubbleOpen, setBubbleOpen] = useState(false);
  const [bubbleMessage, setBubbleMessage] = useState(null);
  const [mood, setMood] = useState('idle');
  const [offset, setOffset] = useState({ x: 0, y: 0 }); // from home corner
  const [facing, setFacing] = useState('left');
  const [mobile, setMobile] = useState(isMobileViewport());
  const [dragging, setDragging] = useState(false);
  const idleTimerRef = useRef(null);
  const tipIndexRef = useRef(0);
  // User-pinned spot (offset coords). While set, roaming is disabled —
  // "se puede mover a placer y se queda donde lo dejas".
  const pinRef = useRef(null);
  const dragStateRef = useRef(null);

  const mascotSize = mobile ? SIZE_MOBILE : SIZE_DESKTOP;
  const margin = mobile ? MARGIN_MOBILE : MARGIN_DESKTOP;
  // Suelo efectivo: en móvil, encima de la barra de navegación.
  const bottomMargin = mobile ? MOBILE_NAV_CLEARANCE : MARGIN_DESKTOP;

  const reducedMotion = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---- Tips (Clippy-style) -------------------------------------------------
  const defaultTips = React.useMemo(() => contextTips || [
    { text: 'Probemos un plan de 7 días con bases del domingo y verduras de temporada 🌱', cta: { label: 'Sí, por favor', open: true } },
    { text: '¿Curioso? Te enseño la estructura 3D de los compuestos del nopal con AlphaFold.', cta: { label: 'Ver estructura', open: true } },
    { text: 'El cuachalalate ayuda con gastritis — te cuento cómo prepararlo bien.', cta: { label: 'Cómo se prepara', open: true } },
    { text: '¿Vas con ansiedad o digestión lenta? Tengo una planta perfecta para ti.', cta: { label: 'Pregúntame', open: true } },
  ], [contextTips]);

  // Home corner = mascot top-left when parked bottom-right.
  const homeXY = useCallback(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    return { homeX: vw - margin - mascotSize, homeY: vh - bottomMargin - mascotSize };
  }, [margin, bottomMargin, mascotSize]);

  // Clamp an offset so the mascot stays fully inside the viewport.
  const clampOffset = useCallback((x, y) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const { homeX, homeY } = homeXY();
    const px = Math.min(Math.max(homeX + x, margin), vw - margin - mascotSize);
    const py = Math.min(Math.max(homeY + y, margin), vh - bottomMargin - mascotSize);
    return { x: Math.round(px - homeX), y: Math.round(py - homeY) };
  }, [homeXY, margin, bottomMargin, mascotSize]);

  // ---- Flight: pick a new perch around the viewport ------------------------
  const pickPerch = useCallback(() => {
    if (typeof window === 'undefined') return { x: 0, y: 0 };
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const { homeX, homeY } = homeXY();
    // Candidate perches as viewport top-left coords (kept near edges so the
    // bird never blocks the middle of the page).
    // En el teléfono solo las esquinas: a 390px de ancho, un colibrí posado
    // a media altura se sienta encima de lo que la persona está leyendo.
    const perches = mobile ? [
      { px: margin, py: Math.max(70, vh * 0.16) },                    // top-left
      { px: vw - margin - mascotSize, py: Math.max(70, vh * 0.14) },  // top-right
      { px: homeX, py: homeY },                                       // home
    ] : [
      { px: margin, py: Math.max(70, vh * 0.16) },                    // top-left
      { px: vw - margin - mascotSize, py: Math.max(70, vh * 0.14) },  // top-right
      { px: margin, py: vh * 0.45 },                                  // mid-left
      { px: vw - margin - mascotSize, py: vh * 0.5 },                 // mid-right
      { px: Math.max(margin, vw * 0.5 - mascotSize / 2), py: Math.max(60, vh * 0.1) }, // top-center
      { px: homeX, py: homeY },                                       // home
    ];
    const choice = perches[Math.floor(Math.random() * perches.length)];
    const clamped = clampOffset(choice.px - homeX, choice.py - homeY);
    return clamped;
  }, [homeXY, clampOffset, margin, mascotSize, mobile]);

  // Resize: track mobile breakpoint; keep a user pin inside the viewport
  // (clamp, don't discard); otherwise snap home so Huitzi never strands.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => {
      setMobile(isMobileViewport());
      if (pinRef.current) {
        const c = clampOffset(pinRef.current.x, pinRef.current.y);
        pinRef.current = c;
        setOffset(c);
      } else {
        setOffset({ x: 0, y: 0 });
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [clampOffset]);

  // ---- Roaming loop (disabled while user-pinned or dragging) ---------------
  useEffect(() => {
    if (!enabled || chatOpen || reducedMotion || dragging) return undefined;
    let cancelled = false;

    const flyToNext = () => {
      if (cancelled || pinRef.current) return; // pinned: stay put
      setOffset((prev) => {
        const next = pickPerch();
        setFacing(next.x < prev.x - 4 ? 'left' : next.x > prev.x + 4 ? 'right' : facing);
        setMood('excited');
        // settle to idle after arriving
        setTimeout(() => { if (!cancelled) setMood('idle'); }, 2600);
        return next;
      });
    };

    const first = setTimeout(flyToNext, 5000);
    const interval = setInterval(flyToNext, 8500);
    return () => { cancelled = true; clearTimeout(first); clearInterval(interval); };
  }, [enabled, chatOpen, reducedMotion, dragging, pickPerch]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the chat opens (or reduced motion), fly home so the chat anchors.
  // On close, return to the user's pinned spot if there is one.
  useEffect(() => {
    if (chatOpen || reducedMotion) {
      setOffset({ x: 0, y: 0 });
      setFacing('left');
    } else if (pinRef.current) {
      setOffset(pinRef.current);
    }
  }, [chatOpen, reducedMotion]);

  // ---- Periodic tip bubble while parked & chat closed ----------------------
  useEffect(() => {
    if (!enabled || chatOpen) return undefined;
    let cancelled = false;
    const showNextTip = () => {
      if (cancelled || chatOpen) return;
      const tip = defaultTips[tipIndexRef.current % defaultTips.length];
      tipIndexRef.current += 1;
      setBubbleMessage(tip);
      setBubbleOpen(true);
      idleTimerRef.current = setTimeout(() => {
        if (!cancelled) setBubbleOpen(false);
      }, 8000);
    };
    const first = setTimeout(showNextTip, 6500);
    const interval = setInterval(showNextTip, 34000);
    return () => {
      cancelled = true;
      clearTimeout(first);
      clearInterval(interval);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [enabled, chatOpen, defaultTips]);

  // ---- Drag to reposition (Pointer Events: mouse + touch) ------------------
  const onPointerDown = useCallback((e) => {
    if (chatOpen) return; // chat anchors home; no dragging while open
    const target = e.currentTarget;
    dragStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseX: offset.x,
      baseY: offset.y,
      moved: false,
      pointerId: e.pointerId,
    };
    try { target.setPointerCapture(e.pointerId); } catch (_) { /* older browsers */ }
  }, [chatOpen, offset]);

  const onPointerMove = useCallback((e) => {
    const d = dragStateRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    if (!d.moved) {
      d.moved = true;
      setDragging(true);
      setBubbleOpen(false);
      setMood('excited');
    }
    e.preventDefault();
    setOffset(clampOffset(d.baseX + dx, d.baseY + dy));
  }, [clampOffset]);

  const onPointerUp = useCallback(() => {
    const d = dragStateRef.current;
    dragStateRef.current = null;
    if (!d) return;
    if (d.moved) {
      // It was a drag: pin where the user dropped him. No chat toggle.
      setDragging(false);
      setMood('idle');
      setOffset((cur) => {
        const isHome = Math.abs(cur.x) < 10 && Math.abs(cur.y) < 10;
        pinRef.current = isHome ? null : cur; // dropping at home un-pins
        return cur;
      });
    } else {
      // It was a tap/click: toggle the chat.
      if (chatOpen) handleCloseChat(); else handleOpenChat();
    }
  }, [chatOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!enabled) return null;

  const handleOpenChat = () => {
    setChatOpen(true);
    setBubbleOpen(false);
    setMood('happy');
    setTimeout(() => setMood('thinking'), 1200);
  };
  const handleCloseChat = () => { setChatOpen(false); setMood('idle'); };
  const handleBubbleDismiss = () => {
    setBubbleOpen(false);
    setMood('idle');
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
  };

  return (
    <div className="huitzi-companion-anchor" style={{ ...anchorStyle, bottom: bottomMargin, right: margin }}>
      {/* Chat panel — anchored to the corner, never flies. Wrapped so it
          re-enables pointer events (anchor is pointer-events:none so the
          empty home box doesn't trap clicks once Huitzi flies away). */}
      <div style={{ pointerEvents: 'auto' }}>
        <HuitziChat
          open={chatOpen}
          onClose={handleCloseChat}
          isAuthenticated={isAuthenticated}
          onAction={onAction}
        />
      </div>

      {/* Flyer — mascot + bubble, translated across the viewport */}
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{
          pointerEvents: 'auto',
          transform: `translate3d(${offset.x}px, ${offset.y}px, 0)`,
          transition: dragging || reducedMotion
            ? 'none'
            : 'transform 3.2s cubic-bezier(0.45, 0, 0.25, 1)',
          willChange: 'transform',
          // touch-action none: the browser must not scroll the page while
          // the user drags Huitzi on a touch screen.
          touchAction: 'none',
          cursor: dragging ? 'grabbing' : 'grab',
        }}
      >
        {/* Tips only while parked at home: a bubble rendered at an edge
            perch can overflow the viewport (the "chat window off-screen"
            bug). At home (bottom-right) it always fits. */}
        {!chatOpen && offset.x === 0 && offset.y === 0 && (
          <HuitziBubble
            open={bubbleOpen}
            message={bubbleMessage?.text}
            actions={
              bubbleMessage?.cta
                ? [
                    { label: bubbleMessage.cta.label, onClick: handleOpenChat, variant: 'primary' },
                    { label: 'Ahora no', onClick: handleBubbleDismiss, variant: 'ghost' },
                  ]
                : []
            }
            onDismiss={handleBubbleDismiss}
          />
        )}

        <HuitziMascot
          mood={mood}
          size={mascotSize}
          facing={facing}
          // Click handled by the pointer-up tap branch above; passing a no-op
          // keeps the mascot's button semantics/ARIA without double-toggling.
          onClick={() => {}}
          ariaLabel={chatOpen ? 'Cerrar chat de Huitzi' : 'Abrir chat de Huitzi (mantén y arrastra para moverlo)'}
        />
      </div>
    </div>
  );
};

const anchorStyle = {
  position: 'fixed',
  zIndex: 9999,
  // pointer-events:none so the (transform-only) empty home box never traps
  // clicks once Huitzi flies away. Children opt back in inline — NOT via a
  // CSS `> *` rule, which inline styles would override (the old bug).
  pointerEvents: 'none',
};

// Inject the typing keyframe used by the chat's typing indicator.
const huitziKeyframes = `
@keyframes huitzi-typing {
  0%, 60%, 100% { opacity: 0.3; }
  30% { opacity: 1; }
}
`;
if (typeof document !== 'undefined' && !document.getElementById('huitzi-companion-style')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'huitzi-companion-style';
  styleEl.textContent = huitziKeyframes;
  document.head.appendChild(styleEl);
}

export default HuitziCompanion;
